import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatCurrency } from "@/domain/billing";
import { formatDate, formatDateRange, toDateOnly } from "@/domain/dates";
import type { InvoiceRecord } from "@/domain/types";
import { drawFooter, drawPdfBrandMark, partyLines, PDF_COLORS } from "./pdf-helpers";

export function downloadReceiptPdf(invoice: InvoiceRecord): void {
  if (invoice.status !== "paid") throw new Error("Only paid invoices can generate a receipt.");
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setFillColor(...PDF_COLORS.charcoal);
  doc.rect(0, 0, 210, 66, "F");
  doc.setFillColor(...PDF_COLORS.mint);
  doc.rect(0, 0, 5, 297, "F");
  drawPdfBrandMark(doc, 14, 9, 13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF_COLORS.white);
  doc.setFontSize(15);
  doc.text(invoice.company.name.toUpperCase(), 31, 18);
  doc.setFontSize(28);
  doc.text("RECEIPT", 194, 20, { align: "right" });
  doc.setTextColor(...PDF_COLORS.gold);
  doc.setFontSize(10);
  doc.text(`For ${invoice.invoiceNumber}`, 194, 29, { align: "right" });

  doc.setGState(doc.GState({ opacity: 0.16 }));
  doc.setTextColor(...PDF_COLORS.mint);
  doc.setFontSize(52);
  doc.text("PAID", 105, 53, { align: "center", angle: -8 });
  doc.setGState(doc.GState({ opacity: 1 }));

  const payment = invoice.payments[0];
  doc.setTextColor(...PDF_COLORS.ink);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("RECEIVED FROM", 16, 79);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(partyLines(invoice.client), 16, 85, { lineHeightFactor: 1.45 });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("PAYMENT DETAILS", 112, 79);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text([
    `Payment date: ${formatDate(payment?.paidAt ?? invoice.paidAt)}`,
    `Method: ${payment?.method ?? "—"}`,
    `Reference: ${payment?.reference || "—"}`,
    `Service period: ${formatDateRange(invoice.serviceStartDate, invoice.serviceEndDate)}`,
  ], 112, 85, { lineHeightFactor: 1.5 });

  autoTable(doc, {
    startY: 116,
    margin: { left: 16, right: 16 },
    head: [["INVOICE", "INVOICE DATE", "SERVICE PERIOD", "AMOUNT RECEIVED"]],
    body: [[invoice.invoiceNumber, formatDate(invoice.issueDate), formatDateRange(invoice.serviceStartDate, invoice.serviceEndDate), formatCurrency(invoice.totals.totalCents, invoice.currency)]],
    theme: "grid",
    headStyles: { fillColor: PDF_COLORS.charcoal2, textColor: PDF_COLORS.white, fontSize: 7.5, fontStyle: "bold" },
    bodyStyles: { textColor: PDF_COLORS.ink, fontSize: 8.5, cellPadding: 4, lineColor: PDF_COLORS.border },
    columnStyles: { 3: { halign: "right", fontStyle: "bold" } },
  });

  const y = ((doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 140) + 14;
  doc.setFillColor(...PDF_COLORS.mintPale);
  doc.roundedRect(90, y, 104, 46, 4, 4, "F");
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF_COLORS.ink);
  doc.setFontSize(9);
  doc.text(`TOTAL RECEIVED (${invoice.currency})`, 98, y + 13);
  doc.setTextColor(...PDF_COLORS.mint);
  doc.setFontSize(22);
  doc.text(formatCurrency(invoice.totals.totalCents, invoice.currency), 186, y + 15, { align: "right" });
  doc.setTextColor(...PDF_COLORS.ink);
  doc.setFontSize(9);
  doc.text("BALANCE DUE", 98, y + 31);
  doc.text(formatCurrency(0, invoice.currency), 186, y + 31, { align: "right" });

  drawFooter(doc, `Receipt · ${invoice.invoiceNumber}`);
  doc.save(`RECEIPT-${invoice.invoiceNumber}.pdf`);
}

export function downloadCombinedReceiptPdf(invoices: InvoiceRecord[]): void {
  const paidInvoices = invoices.filter((invoice) => invoice.status === "paid");
  if (paidInvoices.length === 0) throw new Error("Select at least one paid invoice.");
  const currency = paidInvoices[0]!.currency;
  if (paidInvoices.some((invoice) => invoice.currency !== currency)) throw new Error("Combined receipts must use one currency.");
  const client = paidInvoices[0]!.client;
  if (paidInvoices.some((invoice) => invoice.client.name !== client.name || invoice.client.company !== client.company)) throw new Error("Combined receipts must belong to the same client.");

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setFillColor(...PDF_COLORS.charcoal);
  doc.rect(0, 0, 210, 62, "F");
  doc.setFillColor(...PDF_COLORS.gold);
  doc.rect(0, 0, 5, 297, "F");
  drawPdfBrandMark(doc, 14, 10, 13);
  doc.setTextColor(...PDF_COLORS.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.text("COMBINED RECEIPT", 32, 24);
  doc.setFontSize(9);
  doc.setTextColor(...PDF_COLORS.gold);
  doc.text(`Generated ${formatDate(toDateOnly())}`, 32, 33);
  doc.setGState(doc.GState({ opacity: 0.16 }));
  doc.setTextColor(...PDF_COLORS.mint);
  doc.setFontSize(48);
  doc.text("PAID", 170, 47, { align: "center", angle: -8 });
  doc.setGState(doc.GState({ opacity: 1 }));

  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF_COLORS.ink);
  doc.setFontSize(8);
  doc.text("RECEIVED FROM", 16, 76);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(partyLines(client), 16, 82, { lineHeightFactor: 1.45 });

  autoTable(doc, {
    startY: 112,
    margin: { left: 16, right: 16 },
    head: [["INVOICE", "SERVICE PERIOD", "PAYMENT DATE", "METHOD", "AMOUNT"]],
    body: paidInvoices.map((invoice) => [invoice.invoiceNumber, formatDateRange(invoice.serviceStartDate, invoice.serviceEndDate), formatDate(invoice.payments[0]?.paidAt ?? invoice.paidAt), invoice.payments[0]?.method ?? "—", formatCurrency(invoice.totals.totalCents, invoice.currency)]),
    theme: "grid",
    headStyles: { fillColor: PDF_COLORS.charcoal2, textColor: PDF_COLORS.white, fontSize: 7.2, fontStyle: "bold" },
    bodyStyles: { textColor: PDF_COLORS.ink, fontSize: 8, cellPadding: 3.5, lineColor: PDF_COLORS.border },
    columnStyles: { 4: { halign: "right", fontStyle: "bold" } },
  });
  const total = paidInvoices.reduce((sum, invoice) => sum + invoice.totals.totalCents, 0);
  const y = ((doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 150) + 14;
  doc.setFillColor(...PDF_COLORS.mintPale);
  doc.roundedRect(100, y, 94, 32, 4, 4, "F");
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...PDF_COLORS.ink);
  doc.setFontSize(9);
  doc.text("TOTAL RECEIVED", 108, y + 12);
  doc.setTextColor(...PDF_COLORS.mint);
  doc.setFontSize(20);
  doc.text(formatCurrency(total, currency), 186, y + 14, { align: "right" });
  doc.setTextColor(...PDF_COLORS.ink);
  doc.setFontSize(8.5);
  doc.text(`Balance due: ${formatCurrency(0, currency)}`, 186, y + 24, { align: "right" });
  drawFooter(doc, "Combined receipt");
  doc.save(`COMBINED-RECEIPT-${toDateOnly()}.pdf`);
}
